import { faCamera, faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { Button, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { BottomModal } from '@resurs-internal/mui';
import { type ChangeEvent, useRef } from 'react';

import useTranslation from '../../hooks/useTranslation';
import Icon from '../Icon/Icon';
import { ButtonContainer, ButtonContent, DialogActionsStyles, FileContainer, Input } from './UploadField.styles';

type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
    handleFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onSelectFile?: (file: File) => void;
    removeFile: () => void;
    file?: File | null;
    selectedFile?: File | null;
};

const MobileUploadField = ({
    open,
    setOpen,
    handleFileChange,
    onSelectFile,
    removeFile,
    file,
    selectedFile,
}: Props) => {
    const translate = useTranslation();
    const fileRef = useRef<HTMLInputElement>(null);
    const cameraRef = useRef<HTMLInputElement>(null);

    const getDialogActions = () => {
        switch (true) {
            case !file:
                return (
                    <Button
                        variant="text"
                        onClick={() => {
                            setOpen(false);
                            removeFile();
                        }}
                        color="secondary">
                        {translate('field.upload-document.dialog.cancel')}
                    </Button>
                );
            case file !== selectedFile:
                return (
                    <>
                        <Button
                            variant="text"
                            onClick={() => {
                                setOpen(false);
                                removeFile();
                            }}
                            color="secondary">
                            {translate('field.upload-document.dialog.cancel')}
                        </Button>
                        <Divider sx={{ width: '100%' }} />
                        <Button
                            variant="text"
                            onClick={() => {
                                if (onSelectFile && file) {
                                    onSelectFile(file);
                                    setOpen(false);
                                }
                            }}
                            color="primary">
                            {translate('field.upload-document.dialog.upload')}
                        </Button>
                    </>
                );
            default:
                return (
                    <>
                        <Button
                            variant="text"
                            onClick={() => {
                                setOpen(false);
                                removeFile();
                            }}
                            color="secondary">
                            {translate('field.upload-document.dialog.delete')}
                        </Button>
                        <Divider sx={{ width: '100%' }} />
                        <Button variant="text" onClick={() => fileRef.current?.click()} color="primary">
                            {translate('field.upload-document.dialog.upload-new')}
                        </Button>
                    </>
                );
        }
    };

    return (
        <BottomModal open={open} onClose={() => setOpen(false)}>
            <DialogTitle textAlign={'center'}>{translate('field.upload-document.dialog.header')}</DialogTitle>
            <DialogContent>
                <Input
                    type="file"
                    multiple={false}
                    ref={cameraRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    capture="environment"
                />
                <Input
                    type="file"
                    multiple={false}
                    ref={fileRef}
                    onChange={handleFileChange}
                    accept="image/png, image/jpeg, application/pdf"
                />
                {!file ? (
                    <ButtonContainer>
                        <Button variant="outlined" onClick={() => cameraRef.current?.click()}>
                            <ButtonContent>
                                <Icon icon={faCamera} size="xs" />
                                {translate('field.upload-document.dialog.use-camera')}
                            </ButtonContent>
                        </Button>
                        <Button variant="outlined" onClick={() => fileRef.current?.click()}>
                            <ButtonContent>
                                <Icon icon={faFileLines} size="xs" />
                                {translate('field.upload-document.dialog.choose-from-files')}
                            </ButtonContent>
                        </Button>
                    </ButtonContainer>
                ) : (
                    <FileContainer>
                        <Icon icon={faFileLines} size="xl" />
                        <Typography variant="body1" paddingTop={1}>{`${file?.name}`}</Typography>
                    </FileContainer>
                )}
            </DialogContent>
            <Divider sx={{ width: '100%' }} />
            <DialogActions sx={DialogActionsStyles}>{getDialogActions()}</DialogActions>
        </BottomModal>
    );
};

export default MobileUploadField;
